import { Buffer } from 'buffer';
import {
  call, put, select, takeLatest, takeEvery, all,
} from 'redux-saga/effects';
import callAdapter, { callAdapterResponseError } from '@/utils/sagaHelper';
import findResult, {
  findWithReadStatus,
  findWithCodes,
  successAndErrorsOnDestroy,
  wildcardFilter,
} from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import selectors from '@/selectors';
import { configurationCodes } from '@/configurations/constants';
import SearchService from '../service';
import {
  showFindResults,
  showFindError,
  showSearches,
  showSearchesError,
  showSearch,
  showSearchError,
  newSearch,
  newSearchError,
  editSearch,
  editSearchError,
  destroySearch,
  destroySearchError,
  showSearchConfig,
  showSearchConfigError,
} from '../actions';
import actions, { searchLists } from '../constants';
import { buildFacetRequest } from '../searchHelper';
import buildFilters from '../filtersHelper';

export function* find(action) {
  const items = yield select(selectors.searches);
  const {
    query, pageSize, currentPage, type, filter,
  } = findResult(items, action.category, action.code);
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const searchConfig = yield select(selectors.searchConfig);
  const submittedFilter = buildFilters(filter);
  const facetsToRequest = searchConfig.config.facets
    .filter((facet) => (facet.enabled_sidebar || facet.enabled_dropdown));
  const submittedFacets = buildFacetRequest(facetsToRequest);

  const { response, error } = yield call(callAdapter, SearchService.find(
    query,
    page,
    size,
    type,
    submittedFilter,
    submittedFacets,
  ));

  if (error) {
    yield put(showFindError(action.category, action.code, error));
  } else {
    yield put(showFindResults(
      action.category,
      action.code,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
      response.facets,
      response.info,
      response.warnings,
    ));
  }
}

export function* searches(action) {
  const items = yield select(selectors.searches);
  const {
    display, filter, pageSize, currentPage, privateFlag, internalFlag,
  } = items[action.category];
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const includePrivate = action.privateFlag || privateFlag;
  const incudeInternal = action.internalFlag || internalFlag;
  const { response, error } = yield call(callAdapter, SearchService.searches(
    wildcardFilter(filter),
    includePrivate,
    incudeInternal,
    page,
    display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
  ));

  if (error) {
    yield put(showSearchesError(action.category, error));
  } else {
    yield put(showSearches(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

// saved searches
export function* search(action) {
  const { response, error } = yield call(callAdapter, SearchService.search(action.code));
  if (error) {
    yield put(showSearchError(action.category, action.code, error, action.auto));
  } else {
    yield put(showSearch(action.category, action.code, response));
    if (action.auto) {
      yield find(action);
    }
  }
}

export function* create() {
  const item = yield select(selectors.activeSearch);
  const { response, error } = yield call(callAdapter, SearchService.create(item));
  if (error) {
    yield put(newSearchError(error));
  } else {
    const items = yield select(selectors.searches);
    const toRefresh = findWithReadStatus(items, searchLists, states.SUCCESS);

    yield put(newSearch(response));
    yield all(toRefresh.map((category) => searches({ category })));
  }
}

export function* destroy(action) {
  const results = yield all(action.items.map((i) => callAdapterResponseError(i, SearchService.destroy(i))));
  const { success, errors } = successAndErrorsOnDestroy(results, 'Search not found');

  if (errors.length > 0) {
    yield put(destroySearchError(errors));
  }
  if (success.length > 0) {
    const items = yield select(selectors.searches);
    const codes = success.map((item) => item.code);
    const categories = findWithCodes(items, searchLists, codes);
    const toRefresh = findWithReadStatus(items, categories, states.SUCCESS);

    yield put(destroySearch(success, false));
    yield all(toRefresh.map((category) => searches({ category })));
  }
}

export function* update() {
  const item = yield select(selectors.activeSearch);
  const { response, error } = yield call(callAdapter, SearchService.update(item));
  if (error) {
    yield put(editSearchError(error));
  } else {
    yield put(editSearch(response.code, response));
  }
}

export function* fetchSearchConfig({ category }) {
  const items = yield select(selectors.configurations);
  const code = findResult(items, 'all', configurationCodes.DEFAULT_SEARCH_CONFIG).value.value;
  const { response, error } = yield call(callAdapter, SearchService.config(code));
  if (error) {
    yield put(showSearchConfigError(category, error));
  } else {
    yield put(showSearchConfig(response));
  }
}

export const decodeSearch = (encodedQuery) => {
  try {
    const data = JSON.parse(Buffer.from(encodedQuery, 'base64').toString());
    const includedKeys = Object.keys(data);
    if (
      includedKeys.length <= 3
      && includedKeys.includes('hostname')
      && (includedKeys.includes('filter') || includedKeys.includes('query'))) {
      if (data.hostname === window.location.hostname) {
        return {
          query: data.query || '',
          filter: data.filter || {},
        };
      }
    }
    return { error: 'The decoded search is not valid' };
  } catch (err) {
    return { error: 'Error decoding search string' };
  }
};

export function* decodeSearchQuery({ category, code, query }) {
  const searchParams = decodeSearch(query);

  if (searchParams.error) {
    yield put(showSearchError(category, code, searchParams.error, true));
  } else {
    yield put(showSearch(category, code, searchParams));
  }
}

export function* refreshSearchesPresentation(action) {
  yield call(searches, { ...action, page: 1, append: false });
}

export default function* searchApi() {
  yield takeEvery([actions.FIND, actions.REFRESH_FIND], find);
  yield takeEvery([actions.FETCH_SEARCHES, actions.REFRESH_SEARCHES, actions.UPDATE_SEARCHES_FLAGS], searches);
  yield takeEvery(actions.FETCH_SEARCH, search);
  yield takeLatest(actions.CREATE_SEARCH, create);
  yield takeLatest(actions.UPDATE_SEARCH, update);
  yield takeEvery(actions.DELETE_SEARCH, destroy);
  yield takeLatest(actions.FETCH_SEARCH_CONFIG, fetchSearchConfig);
  yield takeLatest(actions.DECODE_SEARCH_QUERY, decodeSearchQuery);
  yield takeEvery(actions.TOGGLE_SEARCHES_PRESENTATION, refreshSearchesPresentation);
}
